import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import '../styles/Navbar.scss';

// Throttle function to limit the frequency of execution
const throttle = (func: Function, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return func(...args);
  };
};

// Navigation links with type distinction
interface NavLink {
  name: string;
  href: string;
  type: 'anchor' | 'page'; // anchor for hash links, page for regular routes
}

// Updated nav links with Pricing in second position
const navLinks: NavLink[] = [
  { name: 'Home', href: '#home', type: 'anchor' },
  { name: 'Pricing', href: '/pricing', type: 'page' },
  { name: 'Digital Accessibility', href: '#digital-accessibility', type: 'anchor' },
  { name: 'EAA Countdown', href: '#eaa-countdown', type: 'anchor' },
  { name: 'Who We Serve', href: '#who-we-serve', type: 'anchor' },
  { name: 'Compliance Audit', href: '#compliance-audit', type: 'anchor' },
  { name: 'Contact', href: '#contact', type: 'anchor' }
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate(); // Pridajte túto riadku - inicializácia navigate hooku
  const isHomePage = useMemo(() => location.pathname === '/', [location.pathname]);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const firstMobileItemRef = useRef<HTMLAnchorElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  // Track scroll position to highlight the active section
  const handleScroll = useCallback(() => {
    if (!isHomePage) return;

    // First check for sections with direct IDs
    const sections = document.querySelectorAll('section[id]');
    let foundActive = false;

    sections.forEach((section) => {
      const sectionTop = (section as HTMLElement).offsetTop - 100;
      const sectionHeight = (section as HTMLElement).offsetHeight;
      const sectionId = section.getAttribute('id');

      if (window.scrollY >= sectionTop && window.scrollY < sectionTop + sectionHeight) {
        setActiveSection(sectionId || 'home');
        foundActive = true;
      }
    });

    // If no direct section ID matched, check for anchor divs inside sections
    if (!foundActive) {
      // Look for anchor divs with IDs (like in WhoWeServe)
      const anchorDivs = document.querySelectorAll('section .who-we-serve2__anchor[id], section div[id]');

      anchorDivs.forEach((anchor) => {
        const parentSection = anchor.closest('section');
        if (!parentSection) return;

        const sectionTop = (parentSection as HTMLElement).offsetTop - 100;
        const sectionHeight = (parentSection as HTMLElement).offsetHeight;
        const anchorId = anchor.getAttribute('id');

        if (window.scrollY >= sectionTop && window.scrollY < sectionTop + sectionHeight && anchorId) {
          setActiveSection(anchorId);
          foundActive = true;
        }
      });
    }
  }, [isHomePage]);

  // Create throttled version of scroll handler
  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 200), [handleScroll]);

  useEffect(() => {
    if (!isHomePage) return;

    window.addEventListener('scroll', throttledHandleScroll);

    // Initial check on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [isHomePage, throttledHandleScroll, handleScroll]);

  // Manage focus when mobile menu opens/closes
  useEffect(() => {
    if (mobileMenuOpen) {
      // Save the last focused element
      lastFocusedElementRef.current = document.activeElement as HTMLElement;
      // Focus first menu item after a short delay to allow animation
      setTimeout(() => {
        firstMobileItemRef.current?.focus();
      }, 100);
    } else {
      // Return focus when menu closes
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.focus();
      }
    }
  }, [mobileMenuOpen]);

  // Handle escape key for mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen((prevState) => !prevState);
  };

  // Add smooth scroll function
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Function to render the appropriate link type
  const renderNavLink = (link: NavLink, isMobile: boolean = false, index: number = 0) => {
    // For page links (like Pricing)
    if (link.type === 'page') {
      return (
        <Link
          key={link.name}
          to={link.href}
          className={location.pathname === link.href ? 'active' : ''}
          onClick={(e) => {
            // Scroll to top when navigating to page routes
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (isMobile) setMobileMenuOpen(false);
          }}
          aria-current={location.pathname === link.href ? 'page' : undefined}
          ref={isMobile && index === 0 ? firstMobileItemRef : undefined}
          tabIndex={isMobile && !mobileMenuOpen ? -1 : 0}
        >
          {link.name}
        </Link>
      );
    }

    // For anchor links - always use smooth scroll without URL change
    return (
      <a
        key={link.name}
        href={link.href}
        className={isHomePage && activeSection === link.href.replace('#', '') ? 'active' : ''}
        onClick={(e) => {
          e.preventDefault();

          // If not on homepage, navigate to homepage first, then scroll
          if (!isHomePage) {
            navigate('/', { replace: true }); // Použiť navigate namiesto window.location.href
            // Scroll po navigácii s krátkym delay
            setTimeout(() => {
              const targetId = link.href.replace('#', '');
              const targetElement = document.getElementById(targetId);
              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }, 100);
          } else {
            // If on homepage, just scroll
            handleSmoothScroll(e, link.href);
          }

          if (isMobile) setMobileMenuOpen(false);
        }}
        aria-current={isHomePage && activeSection === link.href.replace('#', '') ? 'page' : undefined}
        ref={isMobile && index === 0 ? firstMobileItemRef : undefined}
        tabIndex={isMobile && !mobileMenuOpen ? -1 : 0}
      >
        {link.name}
      </a>
    );
  };

  return (
    <header className="navbar2" role="banner">
      <nav className="navbar2__container" aria-label="Main Navigation">
        <div className="flex-wrapper">
          <div className="navbar2__logo">
            {isHomePage ? (
              <a href="#home" aria-label="MNB Solutions - Back to home">
                <img src="/lovable-uploads/f0f0f6e4-80a7-4016-a26d-c6d7c0a73134.png" alt="MNB Solutions Logo" />
              </a>
            ) : (
              <Link to="/" aria-label="MNB Solutions - Back to home">
                <img src="/lovable-uploads/f0f0f6e4-80a7-4016-a26d-c6d7c0a73134.png" alt="MNB Solutions Logo" />
              </Link>
            )}
          </div>
          <div className="navbar2__desktop-menu">
            <ul className="navbar2__nav-list">
              {navLinks.map((link, index) => (
                <li key={link.name} className="navbar2__nav-item">
                  {renderNavLink(link)}
                </li>
              ))}
            </ul>
          </div>
          <div className="navbar2__mobile-button-wrapper">
            <button
              onClick={toggleMobileMenu}
              className="navbar2__mobile-button"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              ref={mobileButtonRef}
            >
              {mobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div id="mobile-menu" className="navbar2__mobile-menu" ref={mobileMenuRef}>
          <nav aria-label="Mobile Navigation" className="navbar2__mobile-menu-inner">
            <ul className="navbar2__mobile-nav-list">
              {navLinks.map((link, index) => (
                <li key={link.name} className="navbar2__mobile-nav-item">
                  {renderNavLink(link, true, index)}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
